import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api, apiUpload, downloadUrl } from "@/lib/api";
import { AppLayout } from "@/components/AppLayout";
import { toast } from "sonner";
import {
  FolderOpen, Upload, FileText, Pencil, Trash2, Replace, X, ChevronLeft, ChevronRight, Search, FolderTree, Eye,
} from "lucide-react";

const NIVEAU_LABELS = {
  PUBLIC: "Public", ADHERENT: "Adhérents", PRATICIEN: "Praticiens",
  SEMINAIRE_SPECIFIQUE: "Événement", ADMIN: "Admin",
};
const NIVEAU_CLS = {
  PUBLIC: "bg-cfhtb-secondary/10 text-cfhtb-secondary border-cfhtb-secondary/30",
  ADHERENT: "bg-cfhtb-primary/10 text-cfhtb-primary border-cfhtb-primary/30",
  PRATICIEN: "bg-cfhtb-success/10 text-cfhtb-success border-cfhtb-success/30",
  SEMINAIRE_SPECIFIQUE: "bg-cfhtb-warning/10 text-cfhtb-warning border-cfhtb-warning/30",
  ADMIN: "bg-cfhtb-error/10 text-cfhtb-error border-cfhtb-error/30",
};

function formatSize(bytes) {
  if (!bytes) return "—";
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} Ko`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} Mo`;
}

export default function DocumentsAdmin() {
  const [data, setData] = useState(null);
  const [categories, setCategories] = useState([]);
  const [seminaires, setSeminaires] = useState([]);
  const [q, setQ] = useState("");
  const [niveau, setNiveau] = useState("");
  const [page, setPage] = useState(1);
  const [showUpload, setShowUpload] = useState(false);
  const [files, setFiles] = useState([]);
  const [meta, setMeta] = useState({ categorie_id: "", niveau_acces: "ADHERENT", seminar_id: "", tags: "", telechargeable: true });
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewErr, setPreviewErr] = useState(false);

  useEffect(() => {
    if (!preview || preview.extension !== "pdf" || !preview.url_telechargement) return;
    let revoked = null;
    setPreviewErr(false);
    setPreviewUrl(null);
    fetch(downloadUrl(preview.url_telechargement), { credentials: "include" })
      .then((r) => { if (!r.ok) throw new Error(); return r.blob(); })
      .then((b) => { revoked = URL.createObjectURL(b); setPreviewUrl(revoked); })
      .catch(() => setPreviewErr(true));
    return () => { if (revoked) URL.revokeObjectURL(revoked); };
  }, [preview]);

  const openPreview = async (d) => {
    try { setPreview(await api(`/membre/documents/${d.id}`)); }
    catch (e) { toast.error(e.message); }
  };
  const fileRef = useRef(null);
  const replaceRef = useRef(null);
  const [replaceTarget, setReplaceTarget] = useState(null);

  const load = useCallback(() => {
    const qs = new URLSearchParams({ page: String(page), page_size: "20" });
    if (q) qs.set("q", q);
    if (niveau) qs.set("niveau", niveau);
    api(`/admin/documents?${qs}`).then(setData).catch((e) => toast.error(e.message));
  }, [q, niveau, page]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api("/documents/categories").then(setCategories).catch(() => {});
    api("/admin/seminaires").then(setSeminaires).catch(() => {});
  }, []);

  const addFiles = (list) => {
    const arr = Array.from(list || []);
    if (arr.length) setFiles((f) => [...f, ...arr]);
  };

  const uploadAll = async () => {
    setBusy(true);
    let ok = 0, ko = 0;
    for (const f of files) {
      const fd = new FormData();
      fd.append("file", f);
      fd.append("titre", f.name.replace(/\.[^.]+$/, ""));
      fd.append("description", "");
      fd.append("categorie_id", meta.categorie_id);
      fd.append("niveau_acces", meta.niveau_acces);
      fd.append("seminar_id", meta.seminar_id);
      fd.append("tags", meta.tags);
      fd.append("telechargeable", meta.telechargeable ? "true" : "false");
      try {
        await apiUpload("/admin/documents", fd);
        ok++;
      } catch (e) {
        ko++;
        toast.error(`${f.name} : ${e.message}`);
      }
    }
    setBusy(false);
    if (ok) toast.success(`${ok} document(s) téléversé(s)`);
    if (ok && !ko) { setShowUpload(false); setFiles([]); }
    load();
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api(`/admin/documents/${editing.id}`, {
        method: "PUT",
        body: {
          titre: editing.titre, description: editing.description || "",
          categorie_id: editing.categorie_id || null,
          tags: typeof editing.tags === "string" ? editing.tags.split(",").map((t) => t.trim()).filter(Boolean) : editing.tags,
          niveau_acces: editing.niveau_acces,
          seminar_id: editing.seminar_id || null,
          telechargeable: editing.telechargeable !== false,
        },
      });
      toast.success("Document mis à jour");
      setEditing(null);
      load();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  const replaceFile = async (file) => {
    if (!file || !replaceTarget) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const res = await apiUpload(`/admin/documents/${replaceTarget.id}/fichier`, fd);
      toast.success(`Fichier remplacé — version ${res.version}`);
      load();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setReplaceTarget(null);
      if (replaceRef.current) replaceRef.current.value = "";
    }
  };

  const remove = async (d) => {
    if (!window.confirm(`Supprimer « ${d.titre} » ?`)) return;
    try {
      await api(`/admin/documents/${d.id}`, { method: "DELETE" });
      toast.success("Document supprimé");
      load();
    } catch (e) {
      toast.error(e.message);
    }
  };

  const totalPages = data ? Math.max(1, Math.ceil(data.total / data.page_size)) : 1;
  const inputCls = "w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cfhtb-light";
  const labelCls = "block text-sm font-medium text-cfhtb-heading mb-1.5";

  return (
    <AppLayout variant="admin">
      <div data-testid="admin-documents-page" className="space-y-6 animate-fade-in">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <FolderOpen className="h-7 w-7 text-cfhtb-primary" />
            <h1 className="font-serif text-3xl font-bold text-cfhtb-heading tracking-tight">Base documentaire</h1>
          </div>
          <div className="flex gap-2">
            <Link to="/admin/documents/categories" data-testid="doc-categories-button"
              className="inline-flex items-center gap-2 bg-white text-cfhtb-primary border border-cfhtb-primary rounded-md px-4 py-2 text-sm font-medium hover:bg-cfhtb-bg transition-colors">
              <FolderTree className="h-4 w-4" /> Catégories
            </Link>
            <button data-testid="doc-upload-button" onClick={() => setShowUpload(true)}
              className="inline-flex items-center gap-2 bg-cfhtb-primary text-white rounded-md px-4 py-2 text-sm font-medium hover:bg-[#1f1975] transition-colors">
              <Upload className="h-4 w-4" /> Téléverser
            </button>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-cfhtb-light/30 shadow-sm p-4 flex flex-wrap items-end gap-3">
          <form className="flex-1 min-w-[200px]" onSubmit={(e) => { e.preventDefault(); setPage(1); load(); }}>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <input data-testid="admin-doc-search" value={q} onChange={(e) => setQ(e.target.value)}
                placeholder="Rechercher…"
                className="w-full rounded-md border border-slate-300 pl-9 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cfhtb-light" />
            </div>
          </form>
          <select data-testid="admin-doc-filter-niveau" value={niveau} onChange={(e) => { setNiveau(e.target.value); setPage(1); }}
            className="rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cfhtb-light">
            <option value="">Tous niveaux</option>
            {Object.keys(NIVEAU_LABELS).map((n) => <option key={n} value={n}>{NIVEAU_LABELS[n]}</option>)}
          </select>
        </div>

        <div className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="admin-documents-table">
              <thead className="bg-[#F7F7F7] border-b border-slate-200">
                <tr>
                  {["Titre", "Catégorie", "Niveau", "Taille", "Version", "Téléchargements", ""].map((h, i) => (
                    <th key={h} className={`text-xs font-semibold uppercase tracking-wider text-cfhtb-heading px-4 py-3 ${i === 6 ? "text-right" : "text-left"}`}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {!data ? (
                  <tr><td colSpan={7} className="px-4 py-10 text-center text-sm text-cfhtb-text">Chargement…</td></tr>
                ) : data.items.length === 0 ? (
                  <tr><td colSpan={7} className="px-4 py-10 text-center text-sm text-cfhtb-text" data-testid="admin-doc-empty">Aucun document.</td></tr>
                ) : (
                  data.items.map((d) => (
                    <tr key={d.id} className="border-b border-slate-100 hover:bg-cfhtb-bg/50 transition-colors" data-testid={`admin-doc-row-${d.id}`}>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2.5">
                          <FileText className="h-4 w-4 text-cfhtb-light shrink-0" />
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-cfhtb-heading truncate max-w-[280px]">{d.titre}</p>
                            <p className="text-xs text-cfhtb-text/70 uppercase">{d.extension} · {d.nom_fichier}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-cfhtb-text">{d.categorie_nom || "—"}</td>
                      <td className="px-4 py-3">
                        <span className={`text-xs px-2.5 py-0.5 rounded-full font-medium border ${NIVEAU_CLS[d.niveau_acces] || ""}`}>
                          {NIVEAU_LABELS[d.niveau_acces] || d.niveau_acces}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-cfhtb-text">{formatSize(d.taille)}</td>
                      <td className="px-4 py-3 text-sm text-cfhtb-text">v{d.version}</td>
                      <td className="px-4 py-3 text-sm text-cfhtb-text">{d.nb_telechargements}</td>
                      <td className="px-4 py-3 text-right">
                        <div className="inline-flex gap-1">
                          <button data-testid={`doc-preview-${d.id}`} onClick={() => openPreview(d)}
                            className="p-1.5 rounded-md text-cfhtb-text hover:bg-cfhtb-bg hover:text-cfhtb-primary transition-colors" title="Aperçu">
                            <Eye className="h-4 w-4" />
                          </button>
                          <button data-testid={`doc-edit-${d.id}`} onClick={() => setEditing({ ...d, tags: (d.tags || []).join(", ") })}
                            className="p-1.5 rounded-md text-cfhtb-text hover:bg-cfhtb-bg hover:text-cfhtb-primary transition-colors" title="Modifier">
                            <Pencil className="h-4 w-4" />
                          </button>
                          <button data-testid={`doc-replace-${d.id}`} onClick={() => { setReplaceTarget(d); replaceRef.current?.click(); }}
                            className="p-1.5 rounded-md text-cfhtb-text hover:bg-cfhtb-bg hover:text-cfhtb-secondary transition-colors" title="Remplacer le fichier">
                            <Replace className="h-4 w-4" />
                          </button>
                          <button data-testid={`doc-delete-${d.id}`} onClick={() => remove(d)}
                            className="p-1.5 rounded-md text-cfhtb-text hover:bg-cfhtb-error/10 hover:text-cfhtb-error transition-colors" title="Supprimer">
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          <div className="flex items-center justify-between px-4 py-3 border-t border-slate-200">
            <p className="text-xs text-cfhtb-text">{data ? `${data.total} document(s) — page ${page} / ${totalPages}` : ""}</p>
            <div className="flex gap-2">
              <button data-testid="admin-doc-prev" disabled={page <= 1} onClick={() => setPage(page - 1)}
                className="p-1.5 rounded-md border border-slate-300 text-cfhtb-text hover:bg-slate-50 disabled:opacity-40">
                <ChevronLeft className="h-4 w-4" />
              </button>
              <button data-testid="admin-doc-next" disabled={page >= totalPages} onClick={() => setPage(page + 1)}
                className="p-1.5 rounded-md border border-slate-300 text-cfhtb-text hover:bg-slate-50 disabled:opacity-40">
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      </div>

      <input ref={replaceRef} type="file" className="hidden" data-testid="doc-replace-input" onChange={(e) => replaceFile(e.target.files?.[0])} />

      {preview && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4" data-testid="doc-preview-modal">
          <div className="bg-white rounded-lg shadow-lg w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden">
            <div className="flex items-center justify-between px-5 py-3 border-b border-slate-100">
              <h2 className="font-serif text-lg font-semibold text-cfhtb-heading truncate">{preview.titre}</h2>
              <div className="flex items-center gap-2 shrink-0">
                <a href={downloadUrl(preview.url_telechargement)} download={preview.nom_fichier} data-testid="doc-preview-download"
                  className="inline-flex items-center gap-1.5 text-sm text-cfhtb-primary border border-cfhtb-primary rounded-md px-3 py-1.5 hover:bg-cfhtb-bg transition-colors">
                  <FileText className="h-4 w-4" /> Télécharger
                </a>
                <button onClick={() => setPreview(null)} data-testid="doc-preview-close" className="text-cfhtb-text hover:text-cfhtb-heading">
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>
            <div className="flex-1 bg-slate-50">
              {preview.extension !== "pdf" ? (
                <div className="p-10 text-center text-sm text-cfhtb-text" data-testid="doc-preview-modal-noformat">
                  <FileText className="h-10 w-10 mx-auto text-cfhtb-light mb-3" />
                  Aperçu intégré disponible pour les PDF uniquement. Utilisez « Télécharger » pour ce format.
                </div>
              ) : previewErr ? (
                <div className="p-10 text-center text-sm text-cfhtb-text" data-testid="doc-preview-modal-unavailable">
                  <FileText className="h-10 w-10 mx-auto text-cfhtb-light mb-3" />
                  L'aperçu n'est pas disponible (fichier introuvable).
                </div>
              ) : !previewUrl ? (
                <div className="p-10 flex justify-center"><div className="h-8 w-8 border-2 border-cfhtb-primary border-t-transparent rounded-full animate-spin" /></div>
              ) : (
                <iframe src={previewUrl} title={preview.titre} data-testid="doc-preview-modal-iframe" className="w-full h-[70vh]" />
              )}
            </div>
          </div>
        </div>
      )}

      {showUpload && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4" data-testid="doc-upload-modal">
          <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-lg space-y-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between">
              <h2 className="font-serif text-xl font-semibold text-cfhtb-heading">Téléverser des documents</h2>
              <button onClick={() => { setShowUpload(false); setFiles([]); }} data-testid="doc-upload-close" className="text-cfhtb-text hover:text-cfhtb-heading">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div
              data-testid="doc-upload-dropzone"
              onClick={() => fileRef.current?.click()}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => { e.preventDefault(); addFiles(e.dataTransfer.files); }}
              className="cursor-pointer rounded-lg border-2 border-dashed border-cfhtb-light/60 bg-cfhtb-bg/40 hover:bg-cfhtb-bg transition-colors p-8 text-center"
            >
              <Upload className="h-7 w-7 mx-auto text-cfhtb-secondary" />
              <p className="text-sm font-medium text-cfhtb-heading mt-2">Glissez vos fichiers ici ou cliquez</p>
              <p className="text-xs text-cfhtb-text mt-1">PDF, Office, images, ZIP — 20 Mo max par fichier</p>
              <input ref={fileRef} type="file" multiple className="hidden" data-testid="doc-upload-input"
                onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }} />
            </div>
            {files.length > 0 && (
              <ul className="text-xs text-cfhtb-text space-y-1 max-h-28 overflow-y-auto" data-testid="doc-upload-filelist">
                {files.map((f, i) => (
                  <li key={`${f.name}-${f.size}`} className="flex items-center justify-between gap-2 bg-slate-50 rounded px-2 py-1">
                    <span className="truncate">{f.name} <span className="text-cfhtb-text/60">({formatSize(f.size)})</span></span>
                    <button onClick={() => setFiles(files.filter((_, j) => j !== i))} className="text-cfhtb-error"><X className="h-3.5 w-3.5" /></button>
                  </li>
                ))}
              </ul>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className={labelCls}>Catégorie</label>
                <select data-testid="upload-categorie-select" className={inputCls} value={meta.categorie_id} onChange={(e) => setMeta({ ...meta, categorie_id: e.target.value })}>
                  <option value="">Aucune</option>
                  {categories.map((c) => <option key={c.id} value={c.id}>{c.nom}</option>)}
                </select>
              </div>
              <div>
                <label className={labelCls}>Niveau d'accès</label>
                <select data-testid="upload-niveau-select" className={inputCls} value={meta.niveau_acces} onChange={(e) => setMeta({ ...meta, niveau_acces: e.target.value })}>
                  {Object.keys(NIVEAU_LABELS).map((n) => <option key={n} value={n}>{NIVEAU_LABELS[n]}</option>)}
                </select>
              </div>
              {meta.niveau_acces === "SEMINAIRE_SPECIFIQUE" && (
                <div className="sm:col-span-2">
                  <label className={labelCls}>Événement concerné</label>
                  <select data-testid="upload-seminar-select" className={inputCls} value={meta.seminar_id} onChange={(e) => setMeta({ ...meta, seminar_id: e.target.value })}>
                    <option value="">—</option>
                    {seminaires.map((s) => <option key={s.id} value={s.id}>{s.titre}</option>)}
                  </select>
                </div>
              )}
              <div className="sm:col-span-2">
                <label className={labelCls}>Tags (séparés par des virgules)</label>
                <input data-testid="upload-tags-input" className={inputCls} value={meta.tags} onChange={(e) => setMeta({ ...meta, tags: e.target.value })} placeholder="recherche, douleur…" />
              </div>
              <div className="sm:col-span-2">
                <label className="flex items-center gap-2 text-sm text-cfhtb-heading cursor-pointer">
                  <input type="checkbox" data-testid="upload-telechargeable-toggle" checked={meta.telechargeable}
                    onChange={(e) => setMeta({ ...meta, telechargeable: e.target.checked })}
                    className="h-4 w-4 rounded border-slate-300 text-cfhtb-primary" />
                  Téléchargeable depuis l'espace adhérent
                </label>
                <p className="text-xs text-cfhtb-text/70 mt-1 ml-6">Décoché : le document reste consultable à l'écran mais sans bouton de téléchargement.</p>
              </div>
            </div>
            <div className="flex justify-end gap-3">
              <button onClick={() => { setShowUpload(false); setFiles([]); }}
                className="bg-white text-cfhtb-text border border-slate-300 rounded-md px-4 py-2 text-sm font-medium hover:bg-slate-50">
                Annuler
              </button>
              <button data-testid="doc-upload-submit" onClick={uploadAll} disabled={busy || files.length === 0}
                className="bg-cfhtb-primary text-white rounded-md px-4 py-2 text-sm font-medium hover:bg-[#1f1975] disabled:opacity-60">
                {busy ? "Téléversement…" : `Téléverser ${files.length || ""} fichier(s)`}
              </button>
            </div>
          </div>
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4" data-testid="doc-edit-modal">
          <form onSubmit={saveEdit} className="bg-white rounded-lg shadow-lg p-6 w-full max-w-lg space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="font-serif text-xl font-semibold text-cfhtb-heading">Modifier le document</h2>
              <button type="button" onClick={() => setEditing(null)} data-testid="doc-edit-close" className="text-cfhtb-text hover:text-cfhtb-heading">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div>
              <label className={labelCls}>Titre *</label>
              <input required data-testid="edit-titre-input" className={inputCls} value={editing.titre} onChange={(e) => setEditing({ ...editing, titre: e.target.value })} />
            </div>
            <div>
              <label className={labelCls}>Description</label>
              <textarea data-testid="edit-description-input" className={inputCls} rows={3} value={editing.description || ""} onChange={(e) => setEditing({ ...editing, description: e.target.value })} />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className={labelCls}>Catégorie</label>
                <select data-testid="edit-categorie-select" className={inputCls} value={editing.categorie_id || ""} onChange={(e) => setEditing({ ...editing, categorie_id: e.target.value })}>
                  <option value="">Aucune</option>
                  {categories.map((c) => <option key={c.id} value={c.id}>{c.nom}</option>)}
                </select>
              </div>
              <div>
                <label className={labelCls}>Niveau d'accès</label>
                <select data-testid="edit-niveau-select" className={inputCls} value={editing.niveau_acces} onChange={(e) => setEditing({ ...editing, niveau_acces: e.target.value })}>
                  {Object.keys(NIVEAU_LABELS).map((n) => <option key={n} value={n}>{NIVEAU_LABELS[n]}</option>)}
                </select>
              </div>
              {editing.niveau_acces === "SEMINAIRE_SPECIFIQUE" && (
                <div className="sm:col-span-2">
                  <label className={labelCls}>Événement concerné</label>
                  <select data-testid="edit-seminar-select" className={inputCls} value={editing.seminar_id || ""} onChange={(e) => setEditing({ ...editing, seminar_id: e.target.value })}>
                    <option value="">—</option>
                    {seminaires.map((s) => <option key={s.id} value={s.id}>{s.titre}</option>)}
                  </select>
                </div>
              )}
              <div className="sm:col-span-2">
                <label className={labelCls}>Tags</label>
                <input data-testid="edit-tags-input" className={inputCls} value={editing.tags} onChange={(e) => setEditing({ ...editing, tags: e.target.value })} />
              </div>
              <div className="sm:col-span-2">
                <label className="flex items-center gap-2 text-sm text-cfhtb-heading cursor-pointer">
                  <input type="checkbox" data-testid="edit-telechargeable-toggle" checked={editing.telechargeable !== false}
                    onChange={(e) => setEditing({ ...editing, telechargeable: e.target.checked })}
                    className="h-4 w-4 rounded border-slate-300 text-cfhtb-primary" />
                  Téléchargeable depuis l'espace adhérent
                </label>
              </div>
            </div>
            <div className="flex justify-end gap-3">
              <button type="button" onClick={() => setEditing(null)}
                className="bg-white text-cfhtb-text border border-slate-300 rounded-md px-4 py-2 text-sm font-medium hover:bg-slate-50">
                Annuler
              </button>
              <button type="submit" data-testid="doc-edit-save" disabled={busy}
                className="bg-cfhtb-primary text-white rounded-md px-4 py-2 text-sm font-medium hover:bg-[#1f1975] disabled:opacity-60">
                {busy ? "Enregistrement…" : "Enregistrer"}
              </button>
            </div>
          </form>
        </div>
      )}
    </AppLayout>
  );
}
